import { AlertCircle, ArrowLeft, Calendar, Clock, Cpu, RefreshCw, Zap } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getWorkResult, getWorkTask } from '../api/workApi'
import MainLayout from '../components/layout/MainLayout'
import WorkDetailOS from '../components/work/WorkDetailOS'
import WorkDownloadButton from '../components/work/WorkDownloadButton'
import WorkResultViewer from '../components/work/WorkResultViewer'
import WorkStatusBadge from '../components/work/WorkStatusBadge'
import type { WorkResultResponse, WorkTask } from '../types/work'

const SUBCATEGORY_LABELS: Record<string, string> = {
  transcription: 'Transcription',
  translation: 'Translation',
  tts: 'Text to Speech',
  image_gen: 'Image Generation',
  image_analysis: 'Image Analysis',
  content_gen: 'Content Generation',
  summarization: 'Summarization',
  code_gen: 'Code Generation',
  embedding: 'Embedding',
  sentiment: 'Sentiment Analysis',
}

const CATEGORY_LABELS: Record<string, string> = {
  audio_processing: 'Audio Processing',
  image_generation: 'Image Generation',
  content_writing: 'Content & Writing',
  code: 'Code',
  data_analysis: 'Data Analysis',
}

export default function WorkDetail() {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [task, setTask] = useState<WorkTask | null>(null)
  const [result, setResult] = useState<WorkResultResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadTask = useCallback(async () => {
    if (!taskId) {return}
    try {
      const t = await getWorkTask(Number(taskId))
      setTask(t)
      if (t.status === 'completed') {
        const r = await getWorkResult(Number(taskId))
        setResult(r)
      }
    } catch {
      setError('Job not found or access denied.')
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    void loadTask()
  }, [loadTask])

  // Poll while processing
  useEffect(() => {
    if (!task) { return }
    if (task.status !== 'processing' && task.status !== 'pending') {
      if (intervalRef.current) { clearInterval(intervalRef.current) }
      return
    }

    function poll() {
      void (async () => {
        try {
          const updated = await getWorkTask(Number(taskId))
          setTask(updated)
          if (updated.status === 'completed') {
            const r = await getWorkResult(Number(taskId))
            setResult(r)
            if (intervalRef.current) { clearInterval(intervalRef.current) }
          } else if (updated.status === 'failed' || updated.status === 'cancelled') {
            if (intervalRef.current) { clearInterval(intervalRef.current) }
          }
        } catch {
          // silent
        }
      })()
    }

    intervalRef.current = setInterval(poll, 3000)
    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current) }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.status, taskId])

  if (loading) {
    return (
      <MainLayout>
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ height: '200px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      </div>
      </MainLayout>
    )
  }

  if (error || !task) {
    return (
      <MainLayout>
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--danger, #ef4444)', fontSize: '14px' }}>
          <AlertCircle size={16} />
          {error ?? 'Job not found.'}
        </div>
        <button onClick={() => navigate('/work')} style={{ marginTop: '16px', padding: '8px 16px', borderRadius: '8px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
          ← Back to Work
        </button>
      </div>
      </MainLayout>
    )
  }

  const processingMs = task.processing_started_at && task.processing_completed_at
    ? new Date(task.processing_completed_at).getTime() - new Date(task.processing_started_at).getTime()
    : null

  return (
    <MainLayout>
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Back */}
      <button
        onClick={() => navigate('/work')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, alignSelf: 'flex-start' }}
      >
        <ArrowLeft size={14} />
        Back to Work
      </button>

      {/* Header card */}
      <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {task.title}
            </h1>
            <div style={{ fontSize: '13px', color: 'var(--text-muted, #6b7280)' }}>
              {CATEGORY_LABELS[task.category] ?? task.category} · {SUBCATEGORY_LABELS[task.subcategory] ?? task.subcategory}
            </div>
          </div>
          <WorkStatusBadge status={task.status} />
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <MetaItem icon={<Zap size={13} />} label="Cost" value={`${task.flz_cost.toFixed(2)} FLZ`} />
          <MetaItem icon={<Calendar size={13} />} label="Created" value={new Date(task.created_at).toLocaleString()} />
          {task.cf_model && <MetaItem icon={<Cpu size={13} />} label="Model" value={task.cf_model.split('/').pop() ?? task.cf_model} mono />}
          {processingMs !== null && <MetaItem icon={<Clock size={13} />} label="Duration" value={`${(processingMs / 1000).toFixed(1)}s`} />}
        </div>
      </div>

      {/* AIOS Detail Panel — Expert, Anne/Baba, Status Timeline */}
      <WorkDetailOS task={task} result={result} />

      {/* Processing indicator */}
      {(task.status === 'processing' || task.status === 'pending') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderRadius: '12px', background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.2)', fontSize: '13px', color: 'var(--text-secondary)' }}>
          <RefreshCw size={14} style={{ color: 'var(--primary, #6366f1)', animation: 'spin 1.5s linear infinite' }} />
          Processing your job... This page updates automatically.
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
        </div>
      )}

      {/* Result */}
      {task.status === 'completed' && result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <WorkResultViewer result={result} />
          {task.download_url && task.download_expires_at && (
            <WorkDownloadButton taskId={task.id} expiresAt={task.download_expires_at} downloadUrl={task.download_url} />
          )}
        </div>
      )}

      {/* Failed */}
      {task.status === 'failed' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '14px 16px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)', fontSize: '13px', color: 'var(--danger, #ef4444)' }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>Job failed</div>
              <div style={{ opacity: 0.85 }}>{task.error_message ?? 'An unexpected error occurred. Your FLZ has been refunded.'}</div>
            </div>
          </div>
          <button
            onClick={() => navigate('/work/create')}
            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', background: 'var(--primary, #6366f1)', color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
    </MainLayout>
  )
}

function MetaItem({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ color: 'var(--text-muted, #6b7280)' }}>{icon}</span>
      <span style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>{label}:</span>
      <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: mono ? 'monospace' : undefined }}>
        {value}
      </span>
    </div>
  )
}
